import React, { useEffect, useMemo, useState } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBCard,
  MDBCardBody,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
} from 'mdbreact';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import moduloValeNoEscopo from '../../../utils/moduloValeNoEscopo';
import Loading from '../../../components/Loading';
import NavBar from '../../../components/NavBar';
import VinculosPerfil from './VinculosPerfil';
import TelasNovasSemAcesso from './TelasNovasSemAcesso';

// campos-de-tela: asterisco SEMPRE em <span className="red-text">, nunca
// dentro da string do prop `label` (como string ele herda o cinza do label e
// some). Padrão de SEGOV/src/pages/Frota/Contratos/index.js.
// #254: o módulo acompanha o nome só quando desambigua (o backend decide isso),
// e em forma curta — "GESTÃO DE OCORRÊNCIAS" vira "Ocorrências". O título
// completo fica no `title` para quem passar o mouse.
const CONECTORES = ['de', 'da', 'do', 'das', 'dos', 'e'];
const moduloCurto = titulo => {
  if (!titulo) return '';
  return titulo
    .replace(/^GEST[ÃA]O DE\s+/i, '')
    .split(/\s+/)
    .map((palavra, i) => {
      const minuscula = palavra.toLowerCase();
      // conector ANTES de sigla: "DE" tem 2 letras maiúsculas e cairia na regra
      // de sigla, virando "Boletim DE Tráfego"
      if (i > 0 && CONECTORES.indexOf(minuscula) >= 0) return minuscula;
      // sigla continua sigla: LPD não vira "Lpd"
      if (palavra.length <= 4 && palavra === palavra.toUpperCase()) return palavra;
      return minuscula.charAt(0).toUpperCase() + minuscula.slice(1);
    })
    .join(' ');
};

const Obrigatorio = ({ texto }) => (
  <>
    {texto} <span className="red-text">*</span>
  </>
);

const LegendaObrigatorios = () => (
  <p className="grey-text mt-2 mb-0" style={{ fontSize: 12 }}>
    <span className="red-text">*</span> campos obrigatórios
  </p>
);

// ADMINISTRACAO > Perfis de Controle de Acesso
// Cria/edita perfis (ex.: "Coordenador"), define as funcionalidades de cada
// perfil e VINCULA o perfil a usuarios: usuario.perfil_id passa a apontar pra
// ele, adota o que falta (origem='perfil') e nunca mexe no que a pessoa tem
// por fora do perfil (origem='manual'). Mudar o perfil depois nao propaga
// sozinho — "Aplicar alterações" e um segundo ato explicito, com previa (#254).
export default function PerfisAcesso() {
  const [loading, setLoading] = useState(false);
  const [perfis, setPerfis] = useState([]);

  // modal criar/editar perfil
  const [modalPerfil, setModalPerfil] = useState(false);
  const [editandoId, setEditandoId] = useState(null); // null = criando
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);

  // funcionalidades por modulo + selecao do perfil em edicao
  const [modulos, setModulos] = useState([]); // [{id,titulo,gerencias:[],funcionalidades:[]}]
  const [gerenciasComEfetivo, setGerenciasComEfetivo] = useState([]);
  // #348: o que JÁ ESTÁ GRAVADO no perfil, que é a definição de legado que o
  // servidor usa. Não dá para derivar de `funcSelecionadas`: aquilo é o estado
  // do formulário, e as duas coisas divergem assim que alguém marca ou
  // desmarca — a tela travaria o que o servidor aceita, e ofereceria o que ele
  // recusa.
  const [funcLegado, setFuncLegado] = useState({});
  // #354: nomes desmarcados por estreitamento de escopo, mostrados ao lado da
  // causa em vez de num toast que some fora do campo de visão.
  const [desmarcadasPeloEscopo, setDesmarcadasPeloEscopo] = useState([]);
  // #354: legado que a pessoa desmarcou nesta sessão de edição. Depois de
  // salvar, o filtro recusa recolocá-lo — é a única porta de mão única do
  // modal, e merece aviso que não some sozinho.
  const [removidasSemVolta, setRemovidasSemVolta] = useState([]);
  const [funcSelecionadas, setFuncSelecionadas] = useState({}); // {funcId:true}

  // #350: escopo do perfil. Vazio = vale em todas as gerências.
  const [gerenciasDisponiveis, setGerenciasDisponiveis] = useState([]);
  const [gerenciasSel, setGerenciasSel] = useState({}); // {gerenciaId:true}

  // #254: sugestões de perfil derivadas do uso real. A tela EXIBE os
  // agrupamentos de acesso que se repetem no efetivo; criar ou não é decisão de
  // quem administra. Nada aqui grava sozinho.
  const [sugestoes, setSugestoes] = useState([]);
  const [papeis, setPapeis] = useState([]);
  const [carregandoSugestoes, setCarregandoSugestoes] = useState(false);

  // #254: quem são as pessoas de uma sugestão. "97 pessoas" é um número; o RH
  // só consegue julgar se aquilo é um papel de verdade vendo quem são.
  const [modalPessoas, setModalPessoas] = useState(null); // sugestão sendo olhada
  const [modalPapeis, setModalPapeis] = useState(false);
  const [verDetalhe, setVerDetalhe] = useState(false); // a análise crua, dentro do modal

  // modal excluir
  const [idExcluir, setIdExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  // modal aplicar a usuarios
  const [modalAplicar, setModalAplicar] = useState(null); // perfil sendo aplicado
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosSel, setUsuariosSel] = useState({}); // {usuarioId:true}
  // #354: o botão precisa contar quantos foram marcados, e travar em zero.
  const totalUsuariosSel = useMemo(
    () => Object.keys(usuariosSel).filter(k => usuariosSel[k]).length,
    [usuariosSel]
  );
  const [aplicando, setAplicando] = useState(false);

  // propagação de alteração do perfil (#254)
  const [modalPropagar, setModalPropagar] = useState(null); // perfil sendo propagado
  const [previa, setPrevia] = useState(null);
  const [comRemocoes, setComRemocoes] = useState(false); // confirmação separada
  const [propagando, setPropagando] = useState(false);

  const carregarPerfis = async () => {
    setLoading(true);
    try {
      const r = await api.get('/perfil-acesso');
      setPerfis(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Falha ao carregar perfis');
    }
    setLoading(false);
  };

  const carregarModulos = async () => {
    try {
      const r = await api.get('/perfil-acesso-funcionalidades');
      // #348: o payload virou objeto — os módulos agora dizem em quais
      // gerências cada um está liberado, e vem junto a lista de gerências com
      // efetivo, que é o alvo quando o perfil vale em todas.
      setModulos(Array.isArray(r.data.modulos) ? r.data.modulos : []);
      setGerenciasComEfetivo(
        Array.isArray(r.data.gerencias_com_efetivo)
          ? r.data.gerencias_com_efetivo
          : []
      );
    } catch (e) {
      toast.error('Falha ao carregar funcionalidades');
    }
  };

  const carregarSugestoes = async () => {
    setCarregandoSugestoes(true);
    try {
      const r = await api.get('/perfil-acesso-sugestoes');
      setSugestoes((r.data && r.data.sugestoes) || []);
      setPapeis((r.data && r.data.papeis) || []);
    } catch (e) {
      // sugestão é acessório: se falhar, a tela continua utilizável sem ela
      setSugestoes([]);
      setPapeis([]);
    }
    setCarregandoSugestoes(false);
  };

  useEffect(() => {
    carregarPerfis();
    carregarModulos();
    carregarSugestoes();
    // #350: já vem recortada pela subárvore de quem está logado
    // (`estrutura_logica`) — não é a lista completa de gerências.
    api
      .get('/gerencia')
      .then(r => setGerenciasDisponiveis(Array.isArray(r.data) ? r.data : []))
      .catch(() => setGerenciasDisponiveis([]));
  }, []);

  // Abre o modal de criação com o conjunto da sugestão já marcado. O nome fica
  // em branco de propósito: quem batiza o perfil é o RH, não o sistema — a
  // nomenclatura é decisão de negócio, não consequência do agrupamento.
  const criarAPartirDaSugestao = sugestao => {
    const marcadas = {};
    sugestao.funcionalidades.forEach(f => {
      marcadas[f.id] = true;
    });
    setEditandoId(null);
    setNome('');
    setDescricao('');
    // Perfil que ainda não existe não tem nada gravado: legado vazio. Sem
    // isto, os ids pré-marcados pela sugestão passariam por legado e a tela
    // ofereceria o que o servidor vai recusar. Antes de `setFuncSelecionadas`
    // pelo mesmo motivo de ordem explicado em `abrirEdicao`.
    setFuncLegado({});
    setRemovidasSemVolta([]);
    setFuncSelecionadas(marcadas);
    setDesmarcadasPeloEscopo([]);
    // #350: sem isto, criar a partir de uma sugestão herdava em silêncio o
    // escopo do último perfil editado — a sugestão não tem opinião sobre
    // gerência, então o campo começa vazio ("vale em todas").
    setGerenciasSel({});
    setModalPerfil(true);
  };

  // ---- criar / editar perfil ----
  const abrirNovo = () => {
    setEditandoId(null);
    setNome('');
    setDescricao('');
    setFuncLegado({});
    setRemovidasSemVolta([]);
    setFuncSelecionadas({});
    setDesmarcadasPeloEscopo([]);
    setGerenciasSel({});
    setModalPerfil(true);
  };

  const abrirEdicao = async perfil => {
    setEditandoId(perfil.id);
    setNome(perfil.nome || '');
    setDescricao(perfil.descricao || '');
    setModalPerfil(true);
    // carrega as funcionalidades ja associadas ao perfil
    try {
      const r = await api.get(`/perfil-acesso/${perfil.id}`);
      const sel = {};
      (r.data.perfil_funcionalidades || []).forEach(pf => {
        sel[pf.funcionalidade_id] = true;
      });
      // O legado sai da MESMA fonte que o servidor consulta: as linhas
      // gravadas em `perfil_funcionalidade`.
      //
      // A ORDEM IMPORTA: o React 16 não agrupa `setState` depois de um `await`,
      // então cada chamada dispara um render próprio. Com `setFuncSelecionadas`
      // antes, existiria um render com as funcionalidades marcadas e o legado
      // ainda vazio — e o efeito que desmarca o inválido apagaria justamente as
      // que só continuam no perfil por serem legado.
      setFuncLegado(sel);
      setRemovidasSemVolta([]);
      setFuncSelecionadas(sel);
      setDesmarcadasPeloEscopo([]);
      // O `show` devolve `gerencias` como array de ids já transformado. O
      // payload também traz `perfil_gerencias` (o include cru, com
      // `as: 'perfil_gerencias'`), mas leia `gerencias`: é o contrato
      // estável, que não muda se o include mudar de forma.
      const selG = {};
      (r.data.gerencias || []).forEach(gid => {
        selG[gid] = true;
      });
      setGerenciasSel(selG);
    } catch (e) {
      setFuncSelecionadas({});
      setFuncLegado({});
    setRemovidasSemVolta([]);
      setGerenciasSel({});
      setDesmarcadasPeloEscopo([]);
    }
  };

  // #348: quais módulos podem compor ESTE perfil, dado o escopo que está
  // sendo montado no formulário agora. Recalcula a cada gerência marcada, sem
  // ida ao servidor — a recusa de verdade é do backend.
  const escopoSelecionado = useMemo(
    () =>
      Object.keys(gerenciasSel)
        .filter(k => gerenciasSel[k])
        .map(Number),
    [gerenciasSel]
  );

  // #354: gerência inativa por último. Antes a lista abria com a
  // "Coordenadoria de Inteligência (inativa)", dando a ela o lugar de honra.
  const gerenciasOrdenadas = useMemo(
    () =>
      [...gerenciasDisponiveis].sort((a, b) => {
        const ia = a.ativo === false ? 1 : 0;
        const ib = b.ativo === false ? 1 : 0;
        if (ia !== ib) return ia - ib;
        return (a.nome || '').localeCompare(b.nome || '');
      }),
    [gerenciasDisponiveis]
  );

  const modulosPermitidos = useMemo(() => {
    const escopo = escopoSelecionado;
    const permitidos = new Set();
    modulos.forEach(mod => {
      if (moduloValeNoEscopo(mod, escopo, gerenciasComEfetivo)) {
        permitidos.add(mod.id);
      }
    });
    return permitidos;
  }, [modulos, escopoSelecionado, gerenciasComEfetivo]);

  // #348: estreitar o escopo pode invalidar funcionalidade que a pessoa acabou
  // de marcar. Desmarcar sozinho e avisar é melhor que deixar o formulário
  // marcado-e-travado: assim ela não fica sem conseguir desmarcar nem salvar.
  // O legado não entra aqui — ele continua no perfil de propósito.
  useEffect(() => {
    const invalidas = [];
    modulos.forEach(mod => {
      if (modulosPermitidos.has(mod.id)) return;
      (mod.funcionalidades || []).forEach(f => {
        if (funcSelecionadas[f.id] && !funcLegado[f.id]) invalidas.push(f);
      });
    });
    if (invalidas.length === 0) return;
    setFuncSelecionadas(prev => {
      const proximo = { ...prev };
      invalidas.forEach(f => delete proximo[f.id]);
      return proximo;
    });
    // #354: bloco fixo em vez de toast. O toast aparecia no canto superior
    // enquanto os checkboxes afetados estavam ~1400px abaixo — a pessoa nunca
    // via o que tinha perdido. Aqui ele fica logo abaixo da caixa de
    // gerências, que é a causa.
    setDesmarcadasPeloEscopo(invalidas.map(f => f.nome));
  }, [modulosPermitidos, modulos, funcSelecionadas, funcLegado]);

  const toggleFunc = funcId => {
    // #348: tirar do perfil uma funcionalidade que só continua ali por ser
    // legado é porta de mão única — depois de salva, ela não passa mais no
    // filtro e o servidor recusa recolocá-la. Avisar na hora de desmarcar é o
    // mesmo padrão que a tela de Editar usa para o acesso vindo de perfil.
    const estaMarcada = !!funcSelecionadas[funcId];
    const moduloDela = modulos.find(m =>
      (m.funcionalidades || []).some(f => f.id === funcId)
    );
    // #354: o aviso do caso IRREVERSÍVEL era um toast no canto superior,
    // enquanto o do caso reversível (desmarcadas pelo escopo) já tinha virado
    // bloco fixo. Estava invertido: o toast some, e o que some é justamente o
    // que não dá para desfazer.
    if (
      estaMarcada &&
      funcLegado[funcId] &&
      moduloDela &&
      !modulosPermitidos.has(moduloDela.id)
    ) {
      const nome = (moduloDela.funcionalidades || []).find(
        f => f.id === funcId
      );
      setRemovidasSemVolta(prev =>
        prev.includes(nome ? nome.nome : String(funcId))
          ? prev
          : [...prev, nome ? nome.nome : String(funcId)]
      );
    }
    // Remarcar tira da lista — a decisão foi desfeita antes de salvar.
    if (!estaMarcada && funcLegado[funcId]) {
      const nome = (moduloDela && (moduloDela.funcionalidades || []).find(
        f => f.id === funcId
      )) || null;
      if (nome) {
        setRemovidasSemVolta(prev => prev.filter(n => n !== nome.nome));
      }
    }
    setFuncSelecionadas(prev => ({ ...prev, [funcId]: !prev[funcId] }));
  };

  const salvarPerfil = async () => {
    if (nome.trim() === '') {
      toast.error('Informe o nome do perfil');
      return;
    }
    setSalvando(true);
    let perfilRecemCriado = false;
    try {
      let perfilId = editandoId;
      if (editandoId === null) {
        const r = await api.post('/perfil-acesso', {
          nome,
          descricao,
        });
        if (r.data && r.data.erro) {
          toast.error(r.data.erro);
          setSalvando(false);
          return;
        }
        perfilId = r.data.id;
        perfilRecemCriado = true;
      } else {
        const r = await api.put(`/perfil-acesso/${editandoId}`, {
          nome,
          descricao,
        });
        if (r.data && r.data.erro) {
          toast.error(r.data.erro);
          setSalvando(false);
          return;
        }
      }
      // #348: as GERÊNCIAS vão primeiro, e a ordem importa. O servidor valida
      // as funcionalidades contra o escopo gravado do perfil; se as
      // funcionalidades fossem primeiro, marcar uma gerência nova e salvar
      // seria recusado por um escopo que estava a um passo de deixar de valer.
      //
      // #350: escopo vazio é estado válido e significa "vale em todas as
      // gerências" — por isso a chamada acontece sempre, inclusive com lista
      // vazia. Pular quando vazio deixaria o escopo antigo em pé.
      const gerencia_ids = Object.keys(gerenciasSel)
        .filter(k => gerenciasSel[k])
        .map(Number);
      await api.put(`/perfil-acesso/${perfilId}/gerencias`, { gerencia_ids });

      // grava as funcionalidades do perfil
      const funcionalidade_ids = Object.keys(funcSelecionadas)
        .filter(k => funcSelecionadas[k])
        .map(Number);
      await api.put(`/perfil-acesso/${perfilId}/funcionalidades`, {
        funcionalidade_ids,
      });

      toast.success('Perfil salvo');
      setModalPerfil(false);
      carregarPerfis();
    } catch (e) {
      // #254 (achado da revisão da Task 4, I2): o catch genérico jogava fora a
      // mensagem real do servidor — inclusive a lista `funcionalidades_inativas`
      // do guard R5 — e mostrava sempre o mesmo texto. Pior na criação: o POST
      // /perfil-acesso já tinha rodado antes do PUT de funcionalidades falhar,
      // então sobrava um perfil criado com zero funcionalidades enquanto o
      // toast dizia só que a operação falhou, sem indicar que era preciso
      // voltar e editar esse perfil.
      const dados = (e.response && e.response.data) || {};
      // #348: o 400 do servidor traz os ids que barraram. Sem nomeá-los, a
      // pessoa fica olhando 82 checkboxes sem saber qual. É a mesma queixa que
      // o comentário do #254 já registra neste mesmo `catch` a respeito de
      // `funcionalidades_inativas`.
      const nomeDe = id => {
        for (const mod of modulos) {
          const f = (mod.funcionalidades || []).find(x => x.id === id);
          if (f) return f.nome;
        }
        return `#${id}`;
      };
      const barradas = []
        .concat(dados.funcionalidades_fora_do_escopo || [])
        .concat(dados.funcionalidades_inativas || []);
      const mensagem =
        (dados.erro || 'Falha ao salvar o perfil') +
        (barradas.length > 0
          ? `: ${barradas.map(nomeDe).join(', ')}.`
          : '');
      if (perfilRecemCriado) {
        // O POST que cria o perfil ja rodou; o que falhou foi uma das duas
        // gravacoes seguintes. Nao dizemos QUAL, porque a mensagem anterior
        // afirmava "sem funcionalidades" mesmo quando elas tinham sido
        // gravadas e quem falhou foi o escopo — e com a ordem invertida
        // (#348) errar de novo seria fácil.
        toast.error(
          `${mensagem} O perfil foi criado, mas pode ter ficado incompleto — abra a edição e confira as gerências e as funcionalidades.`
        );
        carregarPerfis();
      } else {
        toast.error(mensagem);
      }
    }
    setSalvando(false);
  };

  // ---- excluir ----
  const excluir = async () => {
    if (idExcluir === null) return;
    setExcluindo(true);
    try {
      await api.delete(`/perfil-acesso/${idExcluir}`);
      toast.success('Perfil removido');
      setIdExcluir(null);
      carregarPerfis();
    } catch (e) {
      toast.error('Falha ao remover');
    }
    setExcluindo(false);
  };

  // #255: a coluna `ativo` já existia na tabela `perfil` e nada na tela a usava —
  // a única forma de tirar um perfil de circulação era o DELETE, que é
  // definitivo. Perfil inativo some do seletor de aplicação, mas continua
  // existindo e vinculado a quem já o recebeu.
  const alternarAtivo = async perfil => {
    try {
      await api.put(`/perfil-acesso/${perfil.id}`, { ativo: !perfil.ativo });
      toast.success(perfil.ativo ? 'Perfil desativado' : 'Perfil ativado');
      carregarPerfis();
    } catch (e) {
      toast.error('Falha ao alterar a situação do perfil');
    }
  };

  // ---- aplicar a usuarios ----
  const abrirAplicar = perfil => {
    setModalAplicar(perfil);
    setBuscaUsuario('');
    setUsuarios([]);
    setUsuariosSel({});
  };

  const buscarUsuarios = async () => {
    try {
      const r = await api.get('/perfil-acesso-usuarios', {
        params: { busca: buscaUsuario },
      });
      setUsuarios(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Falha ao buscar usuários');
    }
  };

  const toggleUsuario = id => {
    setUsuariosSel(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // sempre nome + sobrenome (ignora o nome_social)
  const nomeUsuario = u =>
    `${u.nome || ''} ${u.sobrenome || ''}`.trim() || u.nome_social || '—';

  const aplicar = async () => {
    const usuario_ids = Object.keys(usuariosSel)
      .filter(k => usuariosSel[k])
      .map(Number);
    if (usuario_ids.length === 0) {
      toast.error('Selecione ao menos um usuário');
      return;
    }
    setAplicando(true);
    try {
      const r = await api.post(`/perfil-acesso/${modalAplicar.id}/aplicar`, {
        usuario_ids,
      });
      if (r.data && r.data.erro) {
        toast.error(r.data.erro);
      } else {
        toast.success(r.data.ok || 'Perfil atribuído');
        setModalAplicar(null);
        // #354: sem isto, o card continuava dizendo "0 usuário(s)" depois de
        // atribuir com sucesso, e o caminho natural era refazer a operação.
        // Todas as outras ações da tela recarregam; só esta não recarregava.
        carregarPerfis();
      }
    } catch (e) {
      // #354: o 400 do servidor traz `usuarios_fora_do_escopo` com NOME e
      // GERÊNCIA de cada bloqueado, e a tela mostrava "Falha ao aplicar o
      // perfil". Como a operação é tudo-ou-nada, a pessoa ficava olhando N
      // nomes sem saber qual barrou. É o mesmo defeito que já foi corrigido no
      // `salvarPerfil` e não tinha sido replicado aqui.
      const dados = (e.response && e.response.data) || {};
      const bloqueados = dados.usuarios_fora_do_escopo || [];
      toast.error(
        (dados.erro || 'Falha ao atribuir o perfil') +
          (bloqueados.length > 0
            ? `: ${bloqueados
                .map(u => `${u.nome}${u.gerencia ? ` (${u.gerencia})` : ''}`)
                .join(', ')}.`
            : '')
      );
    }
    setAplicando(false);
  };

  // ---- propagar alteração do perfil aos vinculados (#254) ----
  const abrirPropagar = async perfil => {
    setModalPropagar(perfil);
    setPrevia(null);
    setComRemocoes(false);
    try {
      const r = await api.get(`/perfil-acesso/${perfil.id}/previa-propagacao`);
      setPrevia(r.data);
    } catch (e) {
      toast.error('Falha ao calcular a prévia da propagação');
      setModalPropagar(null);
    }
  };

  const propagar = async () => {
    setPropagando(true);
    try {
      const corpo = {
        com_remocoes: comRemocoes,
        // ecoa o hash que a prévia calculou: se o estado (perfil ou acessos
        // de algum vinculado) mudou desde então, o backend recusa com 409 em
        // vez de aplicar um plano que esta tela não mostrou mais (proteção
        // TOCTOU — ver hashPlanoDePropagacao.js no backend).
        plano_hash: previa && previa.plano_hash,
      };
      // escopo parcial: há alvos que o backend não deixa esta pessoa tocar
      // (fora do escopo ou sem lotação). Sem `usuario_ids`, o backend recusa a
      // propagação inteira. A lista de verdade de quem PODE ser tocado é
      // `previa.alvos_ids` — nunca um array vazio, que restringiria a
      // ninguém e devolveria sucesso sem propagar nada.
      const temAlvoForaDoAlcance =
        previa &&
        ((previa.fora_do_seu_escopo && previa.fora_do_seu_escopo.length > 0) ||
          (previa.sem_lotacao && previa.sem_lotacao.length > 0));
      if (temAlvoForaDoAlcance) {
        corpo.usuario_ids = previa.alvos_ids;
      }
      const r = await api.post(`/perfil-acesso/${modalPropagar.id}/propagar`, corpo);
      toast.success(r.data.ok);
      setModalPropagar(null);
      carregarPerfis();
    } catch (e) {
      if (e.response && e.response.status === 409) {
        toast.error(
          'O estado do perfil ou dos usuários vinculados mudou desde a prévia. Feche e abra o diálogo de novo para revisar antes de propagar.'
        );
      } else {
        toast.error(
          (e.response && e.response.data && e.response.data.erro) ||
            'Falha ao propagar o perfil'
        );
      }
    }
    setPropagando(false);
  };

  const qtdFuncs = perfil =>
    perfil.perfil_funcionalidades ? perfil.perfil_funcionalidades.length : 0;

  return (
    <NavBar>
      <MDBContainer>
        <MDBRow className="h4-responsive mt-3" center>
          Perfis de Controle de Acesso
        </MDBRow>
        <TelasNovasSemAcesso />
        <MDBRow center className="mb-2">
          <MDBBtn size="sm" color="mdb-color" onClick={abrirNovo}>
            <MDBIcon fas icon="plus" className="mr-2" />
            Novo Perfil
          </MDBBtn>
          {/* #254: a análise fica ATRÁS de um botão. Montar o catálogo de perfis
              é tarefa ocasional; deixá-la permanente na tela fazia a análise
              ocupar quase o dobro do espaço dos próprios perfis. */}
          {papeis.length > 0 ? (
            <MDBBtn
              color="grey"
              onClick={() => {
                setVerDetalhe(false);
                setModalPapeis(true);
              }}
            >
              Sugerir perfis a partir do uso
            </MDBBtn>
          ) : null}
        </MDBRow>

        {loading ? (
          <Loading />
        ) : perfis.length === 0 ? (
          <MDBRow center className="mt-3 text-danger font-weight-bolder">
            Nenhum perfil cadastrado.
          </MDBRow>
        ) : (
          perfis.map(perfil => (
            <MDBCard className="my-2" key={`perfil${perfil.id}`}>
              {/* #354: o card inativo era o MAIS chamativo da lista, porque o
                  botão ATIVAR verde era o único verde forte quando o Atribuir
                  some. Marcar o estado no FUNDO e numa barra lateral, e não
                  com `opacity`: a opacidade multiplicava com o cinza do texto
                  e derrubava o contraste da descrição para ~1.7:1 — o perfil
                  desativado ficava ilegível, não apenas esmaecido. */}
              <MDBCardBody
                style={
                  perfil.ativo
                    ? undefined
                    : {
                        backgroundColor: '#f8f9fa',
                        borderLeft: '3px solid #ced4da',
                      }
                }
              >
                <MDBRow className="align-items-center">
                  <MDBCol size="12" md="5">
                    <b>{perfil.nome}</b>
                    {perfil.descricao ? (
                      <div className="grey-text" style={{ fontSize: '12px' }}>
                        {perfil.descricao}
                      </div>
                    ) : null}
                  </MDBCol>
                  <MDBCol size="12" md="3">
                    <span className="grey-text" style={{ fontSize: '13px' }}>
                      {qtdFuncs(perfil)} funcionalidade(s)
                    </span>
                    {/* #354: a linha "Ativo/Inativo" saiu. O botão a 300px
                        dali já diz o estado (Desativar / Ativar), e o verde
                        dela era o MESMO do botão Aplicar — a mesma cor
                        significando estado e ação primária na mesma linha. É a
                        coluna redundante ao lado do botão. O card inteiro
                        esmaece quando inativo, o que comunica melhor. */}
                    <div className="grey-text" style={{ fontSize: '12px' }}>
                      {perfil.usuarios_vinculados || 0} usuário(s)
                    </div>
                    {perfil.desatualizados > 0 ? (
                      <div className="orange-text" style={{ fontSize: '12px' }}>
                        ⚠ {perfil.desatualizados} desatualizado(s)
                      </div>
                    ) : null}
                    {/* #350: escopo vazio (`gerencias: []`) precisa ser lido
                        como "vale em todas" — nunca deduzido do campo em
                        branco. Retirar gerência do escopo não desvincula
                        ninguém, então quem ficou fora precisa aparecer aqui,
                        senão o acúmulo fica invisível. */}
                    {/* #354: só quando for exceção. "Vale em todas" é o
                        default e aparecia idêntico em todos os cards —
                        informação que nunca varia não informa. */}
                    {(perfil.gerencias || []).length > 0 ? (
                      <div className="grey-text" style={{ fontSize: '12px' }}>
                        Vale em {perfil.gerencias.length} gerência
                        {perfil.gerencias.length === 1 ? '' : 's'}
                      </div>
                    ) : null}
                    {perfil.vinculados_fora_do_escopo > 0 ? (
                      <div className="red-text" style={{ fontSize: '12px' }}>
                        {perfil.vinculados_fora_do_escopo} vinculado
                        {perfil.vinculados_fora_do_escopo === 1 ? '' : 's'} fora
                        do escopo atual
                      </div>
                    ) : null}
                  </MDBCol>
                  <MDBCol
                    size="12"
                    md="4"
                    className="text-md-right mt-2 mt-md-0"
                  >
                    {/* #354: sempre renderizado, desabilitado quando
                        inativo. Some-lo refluía a fileira e nada se alinhava
                        verticalmente entre os cards. E o nome mudou: "Aplicar"
                        e "Aplicar alterações" no mesmo card faziam coisas
                        muito diferentes — uma vincula pessoas, a outra
                        reescreve o acesso de quem já está vinculado. */}
                    <MDBBtn
                      size="sm"
                      color="success"
                      disabled={!perfil.ativo}
                      onClick={() => abrirAplicar(perfil)}
                    >
                      <MDBIcon fas icon="user-check" className="mr-1" />
                      Atribuir a usuários
                    </MDBBtn>
                    {/* #254: "Aplicar alterações" (propagar) continua visível com o
                        perfil inativo — quem já está vinculado continua vinculado
                        (R4), e revogar/conceder por propagação num perfil fora de
                        circulação é o caminho legítimo para zerar essa exposição. */}
                    {perfil.desatualizados > 0 ? (
                      <MDBBtn
                        size="sm"
                        color="warning"
                        onClick={() => abrirPropagar(perfil)}
                      >
                        Aplicar alterações
                      </MDBBtn>
                    ) : null}
                    <MDBBtn
                      size="sm"
                      color="info"
                      onClick={() => abrirEdicao(perfil)}
                    >
                      <MDBIcon far icon="edit" className="mr-1" />
                      Editar
                    </MDBBtn>
                    <MDBBtn
                      size="sm"
                      color="grey"
                      outline={!perfil.ativo}
                      onClick={() => alternarAtivo(perfil)}
                    >
                      {perfil.ativo ? 'Desativar' : 'Ativar'}
                    </MDBBtn>
                    <MDBBtn
                      size="sm"
                      color="danger"
                      disabled={(perfil.usuarios_vinculados || 0) > 0}
                      title={
                        (perfil.usuarios_vinculados || 0) > 0
                          ? 'Perfil com usuários vinculados não pode ser excluído — desative-o'
                          : 'Excluir perfil'
                      }
                      onClick={() => setIdExcluir(perfil.id)}
                    >
                      <MDBIcon far icon="trash-alt" />
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          ))
        )}

        {!loading && <VinculosPerfil perfis={perfis} />}

        {/* MODAL: PAPÉIS ENCONTRADOS NO USO REAL (#254)
            A gerência lê a CONCLUSÃO — quantos papéis existem e quantas pessoas
            cada um cobre. Os agrupamentos crus, a árvore de continência e os
            encargos ficam atrás de "ver detalhes", para quem quiser conferir. */}
        <MDBModal
          size="lg"
          className="modal-dialog-scrollable"
          isOpen={modalPapeis}
          toggle={() => setModalPapeis(false)}
        >
          <MDBModalHeader toggle={() => setModalPapeis(false)}>
            {papeis.length === 1
              ? '1 papel encontrado no uso do sistema'
              : `${papeis.length} papéis encontrados no uso do sistema`}
          </MDBModalHeader>
          <MDBModalBody>
            <div className="grey-text mb-3" style={{ fontSize: '13px' }}>
              Conjuntos de acesso que se repetem no efetivo. Criar um perfil daqui
              não concede nem remove acesso de ninguém — só cria o perfil com essa
              composição, para você atribuir depois.
            </div>

            {papeis.map((p, i) => (
              <MDBCard className="my-2" key={`papel${i}`}>
                <MDBCardBody>
                  <MDBRow className="align-items-center">
                    <MDBCol size="12" md="8">
                      <b>{p.papel.pessoas} pessoas</b>
                      <span className="grey-text">
                        {' '}
                        têm exatamente este acesso
                        {p.papel.analise && p.papel.analise.contem > p.papel.pessoas
                          ? `, e ${p.papel.analise.contem} já o contêm`
                          : ''}
                      </span>
                      <div style={{ fontSize: '13px' }} className="mt-1">
                        {p.papel.funcionalidades.map((f, j) => (
                          <span key={f.id} title={f.modulo || undefined}>
                            {j > 0 ? <span className="grey-text"> · </span> : null}
                            {f.nome}
                            {f.modulo ? (
                              <span className="grey-text"> ({moduloCurto(f.modulo)})</span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    </MDBCol>
                    <MDBCol size="12" md="4" className="text-md-right mt-2 mt-md-0">
                      <MDBBtn
                        size="sm"
                        color="success"
                        onClick={() => {
                          setModalPapeis(false);
                          criarAPartirDaSugestao(p.papel);
                        }}
                      >
                        <MDBIcon fas icon="plus" className="mr-2" />
                        Criar perfil
                      </MDBBtn>
                    </MDBCol>
                  </MDBRow>
                </MDBCardBody>
              </MDBCard>
            ))}

            <div className="mt-3">
              <button
                type="button"
                className="btn-link p-0"
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '13px',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => setVerDetalhe(!verDetalhe)}
              >
                {verDetalhe
                  ? 'ocultar a análise completa'
                  : `ver a análise completa (${sugestoes.length} agrupamentos)`}
              </button>
            </div>

            {verDetalhe ? (
              <div className="mt-2" style={{ fontSize: '13px' }}>
                {sugestoes.map((sug, i) => (
                  <div
                    key={`det${i}`}
                    className="py-2"
                    style={{
                      marginLeft: `${(sug.profundidade || 0) * 16}px`,
                      borderTop: '1px solid #eee',
                    }}
                  >
                    <b>{sug.pessoas} pessoas</b>
                    {sug.deriva_de ? (
                      <span className="grey-text">
                        {' '}
                        = sugestão de {sug.deriva_de}, mais{' '}
                        {sug.acrescenta.map(f => f.nome).join(', ')}
                      </span>
                    ) : null}
                    {sug.usuarios && sug.usuarios.length > 0 ? (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="btn-link p-0"
                          style={{
                            border: 'none',
                            background: 'none',
                            fontSize: '12px',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setModalPapeis(false);
                            setModalPessoas(sug);
                          }}
                        >
                          ver quem
                        </button>
                      </>
                    ) : null}
                    {sug.analise ? (
                      <div className="grey-text" style={{ fontSize: '12px' }}>
                        {sug.analise.contem} já contêm
                        {sug.analise.contem_com_poucos_extras > 0
                          ? ` · ${sug.analise.contem_com_poucos_extras} com até 3 extras`
                          : ''}
                        {sug.analise.estavel_desde
                          ? ` · desde ${new Date(sug.analise.estavel_desde).toLocaleDateString(
                              'pt-BR',
                              { month: '2-digit', year: 'numeric' }
                            )}`
                          : ''}
                        {sug.analise.extras_comuns && sug.analise.extras_comuns.length > 0
                          ? ` · costumam ter: ${sug.analise.extras_comuns
                              .map(e => `${e.pct}% ${e.nome}`)
                              .join(' · ')}`
                          : ''}
                        {sug.analise.funcoes_misturadas ? (
                          <span className="orange-text">
                            {' '}
                            · ⚠ mistura funções: {sug.analise.funcoes_misturadas.join(', ')}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="grey" onClick={() => setModalPapeis(false)}>
              Fechar
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>

        {/* MODAL: QUEM SÃO AS PESSOAS DE UMA SUGESTÃO (#254) */}
        <MDBModal
          size="xl"
          className="modal-dialog-scrollable"
          isOpen={!!modalPessoas}
          toggle={() => setModalPessoas(null)}
        >
          <MDBModalHeader toggle={() => setModalPessoas(null)}>
            {modalPessoas ? `${modalPessoas.pessoas} pessoa(s) com este acesso` : ''}
          </MDBModalHeader>
          <MDBModalBody>
            {modalPessoas ? (
              <>
                <div className="grey-text mb-3" style={{ fontSize: '13px' }}>
                  {modalPessoas.funcionalidades.map((f, j) => (
                    <span key={f.id}>
                      {j > 0 ? ' · ' : ''}
                      {f.modulo ? `${f.modulo} / ` : ''}
                      {f.nome}
                    </span>
                  ))}
                </div>
                {/* #354: `table-responsive`. A 390px a tabela mede 459px
                    dentro de um diálogo de 359, e o `overflow-x: hidden` do
                    modal cortava as colunas Função e Cargo em silêncio — sem
                    barra de rolagem para alcançá-las. Dado perdido, não
                    apenas escondido.
                    O `maxHeight` saiu: com o rodapé fixo do
                    `modal-dialog-scrollable`, o corpo já rola sozinho, e a
                    caixa interna criava rolagem dentro de rolagem. */}
                <div className="table-responsive">
                  <table className="table table-sm" style={{ fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th>ID func.</th>
                        <th>Nome</th>
                        <th>Gerência</th>
                        <th>Ala</th>
                        <th>Escala</th>
                        <th>Função</th>
                        <th>Cargo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalPessoas.usuarios.map(u => (
                        <tr key={u.id}>
                          <td>{u.id_funcional || '-'}</td>
                          <td>
                            {u.nome}
                            {u.adido ? (
                              <span className="grey-text" title="Adido"> · adido</span>
                            ) : null}
                            {u.seguranca_publica ? (
                              <span className="grey-text" title="Segurança pública"> · seg. pública</span>
                            ) : null}
                          </td>
                          <td>{u.gerencia || '-'}</td>
                          <td>{u.ala || '-'}</td>
                          <td>{u.escala || '-'}</td>
                          <td>{u.funcao || '-'}</td>
                          <td>{u.cargo || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="grey" onClick={() => setModalPessoas(null)}>
              Fechar
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>

        {/* MODAL CRIAR/EDITAR PERFIL */}
        {/* #354: `xl` (1140px), não `fluid` nem `lg`.
            Medido: de 1890px (fluid) até 1140px a altura NÃO sobe um pixel — o
            número de colunas vem do breakpoint do viewport, não da largura do
            modal. Abaixo de 1140 os rótulos começam a quebrar e a altura sobe.
            E 1140 é a largura do `.container` do próprio sistema (os cards da
            listagem medem 1068px), então o modal passa a alinhar com a página
            em vez de transbordar dela.
            `modal-dialog-scrollable` fixa cabeçalho e rodapé: o Salvar sai de
            991px de rolagem (a 1366) para zero, em TODAS as larguras. */}
        <MDBModal
          size="xl"
          className="modal-dialog-scrollable"
          isOpen={modalPerfil}
          toggle={() => setModalPerfil(false)}
        >
          <MDBModalHeader toggle={() => setModalPerfil(false)}>
            {editandoId === null ? 'Novo Perfil' : 'Editar Perfil'}
          </MDBModalHeader>
          <MDBModalBody>
            <MDBRow>
              <MDBCol size="12" md="5">
                <MDBInput
                  label={<Obrigatorio texto="Nome do perfil" />}
                  maxLength={255}
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                />
              </MDBCol>
              <MDBCol size="12" md="7">
                <MDBInput
                  label="Descrição"
                  maxLength={255}
                  value={descricao}
                  onChange={e => setDescricao(e.target.value)}
                />
              </MDBCol>
            </MDBRow>
            <LegendaObrigatorios />

            {/* #350: nenhuma marcada = vale em todas as gerências — o texto
                abaixo da lista nomeia esse estado, para não depender de quem
                lê deduzir a regra a partir do campo em branco. Gerência
                inativa continua podendo compor o escopo, por isso aparece
                marcada como "(inativa)" em vez de sumir da lista. */}
            <div className="mt-3">
              {/* #354: cabeçalho no mesmo peso do irmão "Funcionalidades do
                  perfil". Antes era um `label.grey-text`, peso 300 cinza, que
                  lia como campo desabilitado ao lado de uma seção par em bold
                  preto. */}
              <p className="font-weight-bold mt-2 mb-1">
                Onde este perfil vale
              </p>
              {/* #354: a regra vai ACIMA da caixa e fica SEMPRE visível. Antes
                  ficava embaixo, em 12px cinza, e sumia assim que alguém
                  marcava a primeira gerência — aparecia só no estado em que
                  ninguém precisava dela.

                  E ela precisa dizer as DUAS metades. "Vale em todas" soa
                  permissivo, mas é o estado mais RESTRITIVO para escolher
                  funcionalidades: um perfil que vale em toda parte só aceita
                  funcionalidade liberada em toda parte. Sem essa segunda
                  metade, a pessoa vê módulos travados e não entende por quê. */}
              <div className="grey-text mb-2" style={{ fontSize: '12px' }}>
                {escopoSelecionado.length === 0
                  ? 'Nenhuma marcada: este perfil vale em todas as gerências — e por isso só aceita funcionalidades liberadas em todas elas. Marque gerências para restringir onde ele vale e liberar mais funcionalidades.'
                  : `Só estas ${escopoSelecionado.length} gerências poderão receber este perfil, e ele aceita as funcionalidades liberadas em todas elas. Quem já está vinculado continua como está.`}
              </div>
              {/* #354: sem `maxHeight`. A caixa rolava por dentro de um modal
                  que já rola, escondendo 3 das 9 gerências para economizar
                  93px num modal de 2205px. */}
              <div
                style={{
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  padding: '8px',
                }}
              >
                {/* #354: em grade. Nove linhas empilhadas desperdiçavam a
                    largura do modal e faziam a caixa parecer uma coluna magra
                    dentro de um retângulo largo. */}
                <MDBRow>
                {gerenciasOrdenadas.map(g => (
                  <MDBCol size="12" sm="6" lg="4" key={`gercol${g.id}`}>
                  <div className="custom-control custom-checkbox" key={`ger${g.id}`}>
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id={`ger${g.id}`}
                      checked={!!gerenciasSel[g.id]}
                      onChange={() =>
                        setGerenciasSel(prev => ({
                          ...prev,
                          [g.id]: !prev[g.id],
                        }))
                      }
                    />
                    <label
                      className={`custom-control-label${
                        g.ativo === false ? ' grey-text' : ''
                      }`}
                      htmlFor={`ger${g.id}`}
                      style={{ fontSize: '13px' }}
                    >
                      {g.nome}
                      {g.ativo === false ? ' (inativa)' : ''}
                    </label>
                  </div>
                  </MDBCol>
                ))}
                </MDBRow>
              </div>
            </div>

            {removidasSemVolta.length > 0 ? (
              <div
                className="mt-2"
                style={{
                  fontSize: '12px',
                  borderLeft: '3px solid #d32f2f',
                  paddingLeft: '8px',
                }}
              >
                Vão sair do perfil e <strong>não poderão voltar</strong>{' '}
                enquanto o módulo delas não estiver liberado nas gerências deste
                perfil: <strong>{removidasSemVolta.join(', ')}</strong>
              </div>
            ) : null}
            {desmarcadasPeloEscopo.length > 0 ? (
              <div
                className="grey-text mt-2"
                style={{ fontSize: '12px', borderLeft: '3px solid #ced4da', paddingLeft: '8px' }}
              >
                Desmarcadas porque o módulo delas não está liberado nas
                gerências escolhidas:{' '}
                <strong>{desmarcadasPeloEscopo.join(', ')}</strong>
              </div>
            ) : null}

            <p className="font-weight-bold mt-3 mb-1">
              Funcionalidades do perfil
            </p>
            {modulos.length === 0 ? (
              <p className="grey-text">Carregando funcionalidades...</p>
            ) : (
              /* #354: `mb-4` (24px) em vez de `mb-2` (8px). O espaço ENTRE
                 módulos era menor que o espaço DENTRO deles, então os 13 grupos
                 não existiam para o olho e as 82 caixas liam como um paredão
                 único. */
              modulos.map(mod => (
                <div key={`mod${mod.id}`} className="mb-4">
                  {/* #354: faixa cinza em vez de uma borda `#eee`, que sobre
                      branco dá 1.16:1 e era invisível. A caixa alta normaliza
                      de graça os títulos que vêm inconsistentes do banco
                      ("Administração" ao lado de "ALOCAÇÃO"). */}
                  <div
                    className="font-weight-bold text-uppercase bg-light px-2 py-1"
                    style={{ fontSize: '12px', letterSpacing: '.03em' }}
                  >
                    {mod.titulo}
                    {/* #354: cinza com cadeado, não vermelho. Isto é estado,
                        não erro — o vermelho competia com a lixeira que exclui
                        e com o asterisco de obrigatório, que usam o mesmo tom.
                        E quando TODAS as do módulo já estão no perfil, dizer
                        "não liberado" era enganoso: elas estão ali e
                        funcionando. */}
                    {modulosPermitidos.has(mod.id) ? null : (
                      <span
                        className="grey-text ml-2 text-lowercase"
                        style={{ fontSize: '11px', fontWeight: 'normal' }}
                      >
                        <MDBIcon icon="lock" className="mr-1" />
                        {(mod.funcionalidades || []).every(f => funcLegado[f.id])
                          ? 'continua no perfil por já estar gravado'
                          : 'não liberado em todas as gerências deste perfil'}
                      </span>
                    )}
                  </div>
                  {/* #354: o modal virou `fluid`, então duas colunas
                      deixavam ~660px para um rótulo de 15 caracteres. Com 4
                      colunas em telas largas as 82 funcionalidades cabem em
                      muito menos rolagem, e em telas estreitas volta a uma
                      coluna. */}
                  <MDBRow>
                    {(mod.funcionalidades || []).map(func => (
                      <MDBCol
                        size="12"
                        sm="6"
                        lg="4"
                        xl="3"
                        key={`f${func.id}`}
                        className="mt-1"
                      >
                        {/* padrao Bootstrap custom-control (o input cru fica
                            invisivel/nao-clicavel no tema mdbreact) */}
                        <div
                          className="custom-control custom-checkbox"
                          style={
                            !modulosPermitidos.has(mod.id) &&
                            !funcLegado[func.id]
                              ? { opacity: 0.55 }
                              : undefined
                          }
                        >
                          {/* #348: fora do escopo, o checkbox trava — mas
                              só para quem AINDA não está no perfil. O que já
                              compõe fica editável, porque tirá-lo é decisão de
                              quem administra, e travar marcado deixaria o
                              perfil insalvável. */}
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={`func_${func.id}`}
                            checked={!!funcSelecionadas[func.id]}
                            title={
                              !modulosPermitidos.has(mod.id) &&
                              !funcLegado[func.id]
                                ? `O módulo ${mod.titulo} não está liberado em todas as gerências deste perfil.`
                                : func.rota || undefined
                            }
                            disabled={
                              !modulosPermitidos.has(mod.id) &&
                              !funcLegado[func.id]
                            }
                            onChange={() => toggleFunc(func.id)}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor={`func_${func.id}`}
                            style={{
                              fontSize: '13px',
                              cursor:
                                !modulosPermitidos.has(mod.id) &&
                                !funcLegado[func.id]
                                  ? 'not-allowed'
                                  : 'pointer',
                            }}
                            title={
                              !modulosPermitidos.has(mod.id) &&
                              !funcLegado[func.id]
                                ? `O módulo ${mod.titulo} não está liberado em todas as gerências deste perfil.`
                                : func.rota || undefined
                            }
                          >
                            {/* #354: o rótulo por item saiu, e a rota
                                também. O cabeçalho do módulo já diz o estado —
                                repetir por item punha quatro avisos sobre o
                                mesmo fato num bloco de três linhas. E a rota
                                era ruído técnico para quem administra: 82
                                delas, 14 sem barra inicial, uma exibindo
                                literalmente "(sem rota)". Fica no `title`,
                                para quem der suporte. */}
                            {func.nome}
                            {/* #354/#348: marca de volta. Sem ela, o item que
                                só continua no perfil por ser legado — e que
                                NÃO pode ser recolocado depois de removido —
                                ficava visualmente idêntico a qualquer outro
                                marcado. O `opacity` do wrapper não o alcança,
                                de propósito, porque ele é editável. */}
                            {!modulosPermitidos.has(mod.id) &&
                            funcLegado[func.id] ? (
                              <MDBIcon
                                icon="lock"
                                className="grey-text ml-1"
                                style={{ fontSize: '10px' }}
                                title="Só continua no perfil por já estar gravado. Se você remover e salvar, não poderá recolocar."
                              />
                            ) : null}
                          </label>
                        </div>
                      </MDBCol>
                    ))}
                  </MDBRow>
                </div>
              ))
            )}
          </MDBModalBody>
          <MDBModalFooter between>
            {/* #354: cinza, não vermelho preenchido — era a mesma cor da
                lixeira que exclui o perfil. */}
            <MDBBtn
              color="grey"
              size="sm"
              onClick={() => setModalPerfil(false)}
            >
              Cancelar
            </MDBBtn>
            <MDBBtn
              color="success"
              size="sm"
              disabled={salvando}
              onClick={salvarPerfil}
            >
              <MDBIcon fas icon="save" className="mr-2" />
              {salvando ? 'Salvando...' : 'Salvar'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>

        {/* MODAL EXCLUIR */}
        <MDBModal
          isOpen={idExcluir !== null}
          toggle={() => setIdExcluir(null)}
        >
          <MDBModalHeader toggle={() => setIdExcluir(null)}>
            Excluir Perfil
          </MDBModalHeader>
          <MDBModalBody className="text-danger font-weight-bolder">
            Tem certeza que deseja excluir este perfil? (não altera os acessos
            já aplicados aos usuários)
          </MDBModalBody>
          <MDBModalFooter between>
            <MDBBtn color="secondary" onClick={() => setIdExcluir(null)}>
              Cancelar
            </MDBBtn>
            <MDBBtn color="danger" disabled={excluindo} onClick={excluir}>
              {excluindo ? 'Excluindo...' : 'Excluir'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>

        {/* MODAL APLICAR A USUARIOS */}
        {/* #354: rodapé fixo — a 390px a lista de usuários deixa o botão a
            centenas de px de rolagem. */}
        <MDBModal
          size="lg"
          className="modal-dialog-scrollable"
          isOpen={modalAplicar !== null}
          toggle={() => setModalAplicar(null)}
        >
          <MDBModalHeader toggle={() => setModalAplicar(null)}>
            Atribuir perfil {modalAplicar ? `"${modalAplicar.nome}"` : ''} a usuários
          </MDBModalHeader>
          <MDBModalBody>
            <MDBRow className="align-items-center">
              <MDBCol size="12" md="8">
                <MDBInput
                  label="Buscar usuário (nome)"
                  value={buscaUsuario}
                  onChange={e => setBuscaUsuario(e.target.value)}
                />
              </MDBCol>
              <MDBCol size="12" md="4">
                <MDBBtn size="sm" color="mdb-color" onClick={buscarUsuarios}>
                  <MDBIcon fas icon="search" className="mr-2" />
                  Buscar
                </MDBBtn>
              </MDBCol>
            </MDBRow>

            <div className="grey-text mb-3" style={{ fontSize: '13px' }}>
              Atribuir o perfil concede as funcionalidades que faltam e
              vincula a pessoa ao perfil. Acessos concedidos por fora do
              perfil não são alterados.
            </div>

            <div className="mt-2" /* #354: sem `maxHeight` — o rodapé fixo já dá a rolagem, e 40vh
                    consumia 60% da tela num celular, criando rolagem dentro
                    de rolagem. */>
              {usuarios.length === 0 ? (
                <p className="grey-text mt-2">
                  Busque usuários pelo nome para selecionar.
                </p>
              ) : (
                usuarios.map(u => (
                  <div
                    key={`u${u.id}`}
                    className="custom-control custom-checkbox mb-1"
                  >
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id={`user_${u.id}`}
                      checked={!!usuariosSel[u.id]}
                      onChange={() => toggleUsuario(u.id)}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`user_${u.id}`}
                      style={{ fontSize: '13px' }}
                    >
                      {nomeUsuario(u)}
                    </label>
                  </div>
                ))
              )}
            </div>
          </MDBModalBody>
          <MDBModalFooter between>
            <MDBBtn
              color="grey"
              size="sm"
              onClick={() => setModalAplicar(null)}
            >
              Cancelar
            </MDBBtn>
            {/* #354: o botão dizia só "Aplicar" e ficava habilitado com
                zero usuários marcados. Agora conta e trava. */}
            <MDBBtn
              color="success"
              size="sm"
              disabled={aplicando || totalUsuariosSel === 0}
              onClick={aplicar}
            >
              <MDBIcon fas icon="user-check" className="mr-2" />
              {aplicando
                ? 'Atribuindo...'
                : totalUsuariosSel === 0
                ? 'Selecione ao menos um usuário'
                : `Atribuir a ${totalUsuariosSel} usuário${
                    totalUsuariosSel === 1 ? '' : 's'
                  }`}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>

        {/* MODAL APLICAR ALTERAÇÕES (propagação — #254) */}
        {/* #354: `lg` e rodapé fixo. Era o modal com MAIS informação da tela
            (três listas mais três avisos) e o único sem `size` — 500px. Pior: o
            botão que confirma a revogação ficava a ~805px de rolagem num
            celular. Agora ele fica sempre visível. */}
        <MDBModal
          size="lg"
          className="modal-dialog-scrollable"
          isOpen={!!modalPropagar}
          toggle={() => setModalPropagar(null)}
        >
          <MDBModalHeader toggle={() => setModalPropagar(null)}>
            Aplicar alterações — {modalPropagar ? modalPropagar.nome : ''}
          </MDBModalHeader>
          <MDBModalBody>
            {!previa ? (
              <Loading />
            ) : (
              <>
                <div className="mb-3">
                  <b>{previa.alvos}</b> de {previa.total_vinculados} usuário(s)
                  vinculado(s) serão alterados.
                </div>

                <div className="mb-2">
                  <b className="green-text">CONCEDER</b>
                  {previa.concessoes.length === 0 ? (
                    <div className="grey-text">nada a conceder</div>
                  ) : (
                    previa.concessoes.map(c => (
                      <div key={`c${c.funcionalidade_id}`}>
                        + {c.nome || `funcionalidade ${c.funcionalidade_id}`}{' '}
                        — {c.usuarios} usuário(s)
                      </div>
                    ))
                  )}
                </div>

                <div className="mb-2 grey-text">
                  <b>PRESERVAR</b>
                  <div>
                    {previa.manuais_preservados} acesso(s) concedido(s) por fora do
                    perfil — não serão alterados
                  </div>
                </div>

                {previa.revogacoes.length > 0 ? (
                  <div className="mb-2">
                    <MDBInput
                      type="checkbox"
                      id="comRemocoes"
                      label="REMOVER os acessos que saíram do perfil"
                      checked={comRemocoes}
                      onChange={() => setComRemocoes(!comRemocoes)}
                    />
                    {previa.revogacoes.map(r => (
                      <div key={`r${r.funcionalidade_id}`} className="red-text">
                        − {r.nome || `funcionalidade ${r.funcionalidade_id}`}{' '}
                        — {r.usuarios} usuário(s)
                      </div>
                    ))}
                    {previa.restauracoes > 0 ? (
                      <div className="orange-text mt-2" style={{ fontSize: '13px' }}>
                        ⚠ {previa.restauracoes} usuário(s) receberão de volta acesso
                        que havia sido removido à mão.
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {/* fora_do_seu_escopo (403 — negativa clara de escopo) e
                    sem_lotacao (404 — não dá pra saber o escopo) têm causas
                    diferentes; textos separados para a mensagem não confundir
                    as duas. Em ambos os casos a propagação segue só para
                    quem está em previa.alvos_ids. */}
                {previa.fora_do_seu_escopo.length > 0 ? (
                  <div className="orange-text mt-3" style={{ fontSize: '13px' }}>
                    {previa.fora_do_seu_escopo.length} usuário(s) com este perfil
                    estão fora do seu escopo e não serão alterados.
                  </div>
                ) : null}
                {previa.sem_lotacao.length > 0 ? (
                  <div className="orange-text mt-2" style={{ fontSize: '13px' }}>
                    {previa.sem_lotacao.length} usuário(s) com este perfil não têm
                    lotação/gerência definida — não é possível saber se estão no
                    seu escopo, então não serão alterados.
                  </div>
                ) : null}
              </>
            )}
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="grey" onClick={() => setModalPropagar(null)}>
              Cancelar
            </MDBBtn>
            <MDBBtn color="success" disabled={!previa || propagando} onClick={propagar}>
              {propagando ? 'Propagando…' : 'Propagar'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>
      </MDBContainer>
    </NavBar>
  );
}
